"use client";

import React, { useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const roadmapStages = [{
  title: "Deep Assessment",
  description: "We conduct a granular assessment of your assets, utilizing a magnifying-glass approach to uncover hidden opportunities in your portfolio."
}, {
  title: "Strategic Blueprint",
  description: "Our team crafts a bespoke strategic blueprint, mapping out non-traditional pathways to compound wealth while securing your family's future."
}, {
  title: "Wealth Activation",
  description: "We implement your creative finance plan, acting as the gold key that unlocks tax-advantaged growth and lifetime guaranteed income."
}, {
  title: "Legacy Alchemy",
  description: "Continuous refinement ensures your financial architecture evolves alongside market shifts and your personal growth milestones for lasting generational wealth."
}];
export default function YourFinancialRoadmap() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const timelineReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="92385" id="the-conversion-process" className="dark bg-background py-24 sm:py-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerReveal.ref} className={`mx-auto max-w-2xl text-center transition-all duration-1000 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-base/7 font-semibold text-primary uppercase tracking-widest">
            The Methodology
          </h2>
          <p className="mt-2 text-4xl font-serif tracking-tight text-foreground sm:text-5xl">
            The Conversion Process
          </p>
          <p className="mt-6 text-lg/8 text-muted-foreground font-sans font-light">
            A bespoke, three-dimensional strategy designed to transform traditional assets into high-performance wealth engines with total clarity and confidence.
          </p>
        </div>

        <div ref={timelineReveal.ref} className={`mx-auto mt-20 max-w-5xl relative transition-all duration-1000 delay-300 ease-out ${timelineReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          {}
          <div className="hidden md:block absolute top-12 left-12 right-12 h-[2px] border-t-2 border-dotted border-primary/20 -z-10" />
          {}
          <div className="md:hidden absolute top-12 bottom-12 left-12 w-[2px] border-l-2 border-dotted border-primary/20 -z-10" />

          <div className="flex flex-col md:flex-row gap-12 md:gap-4 justify-between">
            <TooltipProvider delayDuration={200}>
              {roadmapStages.map((stage, i) => <div key={i} data-index={i} className="relative flex-1 flex flex-row md:flex-col items-center md:items-center gap-6 group">
                  {}
                  <div className="size-24 bg-background flex items-center justify-center shrink-0 transition-transform duration-500 group-hover:scale-105">
                    <span className="font-mono text-6xl font-bold text-primary/20 transition-colors duration-500 group-hover:text-primary/60">
                      0{i + 1}
                    </span>
                  </div>

                  {}
                  <div className="flex-1 md:w-full text-left md:text-center">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="inline-flex items-center gap-2 text-xl font-medium text-foreground hover:text-primary transition-colors cursor-help outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md">
                          {stage.title}
                          <Info className="size-4 text-muted-foreground/50 group-hover:text-primary/70 transition-colors" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" sideOffset={12} className="max-w-[260px] text-center p-4 shadow-2xl bg-card border-border">
                        <p className="text-sm leading-relaxed text-card-foreground">
                          {stage.description}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                    
                    {}
                    <p className="mt-2 text-sm text-muted-foreground md:hidden pr-4">
                      {stage.description}
                    </p>
                  </div>
                </div>)}
            </TooltipProvider>
          </div>
        </div>
      </div>
    </section>;
}